import Echo from 'laravel-echo'
import Pusher from 'pusher-js'

/**
 * plugins/echo.js — Modul 18: Payment Gateway & Realtime Broadcast
 * ─────────────────────────────────────────────────────────────────
 * Dua instance Echo dipisah karena dua cara otorisasi channel berbeda
 * (lihat routes/channels.php di backend):
 *
 *  1. staffEcho   -> private-tenant.{tenantId}.pos / .station.{stationId}
 *                    otorisasi via cookie Sanctum (accessToken) + X-Tenant.
 *  2. selfOrderEcho -> presence-self-order.{sessionToken}
 *                    otorisasi via header X-Session-Token (TANPA Sanctum),
 *                    dipakai di halaman customer-facing self-order.
 *
 *  3. posDisplayEcho -> private-tenant.{tenantId}.pos-display.{shiftId}
 *                    (Modul AR, Addendum 11 §1) otorisasi via header
 *                    X-Pos-Display-Token (TANPA Sanctum), dipakai Customer
 *                    Display Screen setelah pairing lewat pos-display/connect.
 *
 * ADDENDUM 5A.2: kita baca cookie langsung (bukan localStorage).
 * ADDENDUM 5A.4: base URL & pola parsing response mengikuti useApi().
 */

window.Pusher = Pusher

const PUSHER_KEY = import.meta.env.VITE_PUSHER_APP_KEY
const PUSHER_CLUSTER = import.meta.env.VITE_PUSHER_APP_CLUSTER || 'ap1'
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || ''

function getCookie(name) {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  
  return match ? decodeURIComponent(match[1]) : null
}

let staffEchoInstance = null
let selfOrderEchoInstance = null

/**
 * Inisialisasi Echo untuk sisi STAF (POS Kasir / KDS / dsb).
 * Dipanggil sekali setelah login berhasil (mis. di auth store / App.vue),
 * dan di-disconnect saat logout.
 */
export function initStaffEcho() {
  if (staffEchoInstance) return staffEchoInstance

  if (!PUSHER_KEY) {
    console.warn('[echo] VITE_PUSHER_APP_KEY belum di-set — realtime dinonaktifkan.')
    
    return null
  }

  staffEchoInstance = new Echo({
    broadcaster: 'pusher',
    key: PUSHER_KEY,
    cluster: PUSHER_CLUSTER,
    forceTLS: true,
    authEndpoint: `${API_BASE_URL}/broadcasting/auth`,
    authorizer: channel => ({
      authorize: (socketId, callback) => {
        fetch(`${API_BASE_URL}/broadcasting/auth`, {
          method: 'POST',
          credentials: 'include', // WAJIB: kirim cookie accessToken (5A.2)
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Tenant': getCookie('current_tenant') || '',
          },
          body: JSON.stringify({
            socket_id: socketId,
            channel_name: channel.name,
          }),
        })
          .then(res => {
            if (!res.ok) throw new Error(`Broadcast auth gagal (${res.status})`)
            
            return res.json()
          })
          .then(data => callback(null, data))
          .catch(err => callback(err, null))
      },
    }),
  })

  return staffEchoInstance
}

export function getStaffEcho() {
  return staffEchoInstance
}

export function disconnectStaffEcho() {
  staffEchoInstance?.disconnect()
  staffEchoInstance = null
}

/**
 * Inisialisasi Echo untuk sisi SELF-ORDER (pelanggan, tanpa Sanctum).
 * Otorisasi presence channel memakai X-Session-Token, BUKAN cookie staf.
 * Dipanggil dari halaman self-order setelah session_token didapat.
 */
export function initSelfOrderEcho(sessionToken) {
  if (selfOrderEchoInstance) disconnectSelfOrderEcho()

  if (!PUSHER_KEY) {
    console.warn('[echo] VITE_PUSHER_APP_KEY belum di-set — realtime dinonaktifkan.')
    
    return null
  }

  selfOrderEchoInstance = new Echo({
    broadcaster: 'pusher',
    key: PUSHER_KEY,
    cluster: PUSHER_CLUSTER,
    forceTLS: true,
    authEndpoint: `${API_BASE_URL}/broadcasting/auth`,
    authorizer: channel => ({
      authorize: (socketId, callback) => {
        fetch(`${API_BASE_URL}/broadcasting/auth`, {
          method: 'POST',
          credentials: 'omit', // pelanggan self-order tidak pakai cookie staf
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Session-Token': sessionToken,
          },
          body: JSON.stringify({
            socket_id: socketId,
            channel_name: channel.name,
          }),
        })
          .then(res => {
            if (!res.ok) throw new Error(`Broadcast auth gagal (${res.status})`)
            
            return res.json()
          })
          .then(data => callback(null, data))
          .catch(err => callback(err, null))
      },
    }),
  })

  return selfOrderEchoInstance
}

export function getSelfOrderEcho() {
  return selfOrderEchoInstance
}

export function disconnectSelfOrderEcho() {
  selfOrderEchoInstance?.disconnect()
  selfOrderEchoInstance = null
}

let posDisplayEchoInstance = null

/**
 * Inisialisasi Echo untuk sisi Customer Display Screen (Modul AR, Addendum
 * 11 §1). Otorisasi private channel memakai header X-Pos-Display-Token
 * (token dari pos-display/connect), BUKAN cookie staf ataupun session_token
 * self-order — lihat routes/channels.php & AppServiceProvider (guard
 * `pos_display`) di backend.
 */
export function initPosDisplayEcho(token) {
  if (posDisplayEchoInstance) disconnectPosDisplayEcho()

  if (!PUSHER_KEY) {
    console.warn('[echo] VITE_PUSHER_APP_KEY belum di-set — realtime dinonaktifkan.')
    
    return null
  }

  posDisplayEchoInstance = new Echo({
    broadcaster: 'pusher',
    key: PUSHER_KEY,
    cluster: PUSHER_CLUSTER,
    forceTLS: true,
    authEndpoint: `${API_BASE_URL}/broadcasting/auth`,
    authorizer: channel => ({
      authorize: (socketId, callback) => {
        fetch(`${API_BASE_URL}/broadcasting/auth`, {
          method: 'POST',
          credentials: 'omit', // device CDS tidak pakai cookie staf
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Pos-Display-Token': token,
          },
          body: JSON.stringify({
            socket_id: socketId,
            channel_name: channel.name,
          }),
        })
          .then(res => {
            if (!res.ok) throw new Error(`Broadcast auth gagal (${res.status})`)
            
            return res.json()
          })
          .then(data => callback(null, data))
          .catch(err => callback(err, null))
      },
    }),
  })

  return posDisplayEchoInstance
}

export function getPosDisplayEcho() {
  return posDisplayEchoInstance
}

export function disconnectPosDisplayEcho() {
  posDisplayEchoInstance?.disconnect()
  posDisplayEchoInstance = null
}

export function getEcho() {
  return getStaffEcho()
}
